"use client";

import { useState } from "react";

import { analytics } from "@/lib/analytics";
import { cn } from "@/lib/utils";

import {
  sortRomaReserveLocations,
  type RomaReserveLocation,
} from "./roma/roma-types";

type RomaReservationWidgetProps = {
  locations: readonly RomaReserveLocation[];
  className?: string;
  variant?: "light" | "dark";
};

export function RomaReservationWidget({
  locations,
  className = "",
  variant = "light",
}: RomaReservationWidgetProps) {
  const sortedLocations = sortRomaReserveLocations(locations);
  const defaultSlug =
    sortedLocations.find((location) => location.slug === "southern-highlands")
      ?.slug ?? sortedLocations[0]?.slug ?? "";
  const [locationSlug, setLocationSlug] = useState(defaultSlug);
  const selectedLocation =
    sortedLocations.find((location) => location.slug === locationSlug) ??
    sortedLocations[0];
  const isLight = variant === "light";

  if (!selectedLocation) return null;

  return (
    <section
      id="roma-reservation-widget"
      aria-label="Roma reservation location chooser"
      data-funnel-priority="primary-reservation"
      className={cn(
        "scroll-mt-28 lg:scroll-mt-32",
        isLight
          ? "rounded-[16px] border border-[#eadbd3] bg-[#fffaf7] p-4 text-[#1f1f1f] shadow-[0_18px_36px_-30px_rgba(69,42,28,0.35)] sm:p-5"
          : "border border-[var(--roma-terracotta)]/24 bg-[rgba(255,250,235,0.08)] p-3.5 text-white shadow-[0_28px_80px_-64px_rgba(0,0,0,0.9)] backdrop-blur sm:p-5",
        className,
      )}
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[var(--roma-terracotta)]">
        Roma Kitchen · {selectedLocation.eyebrow}
      </p>
      <h2 className={cn("mt-2 font-serif text-3xl", isLight ? "text-[#151515]" : "font-normal text-white")}>
        Reserve your table.
      </h2>
      <p className={cn("mt-2 text-sm", isLight ? "text-[#5b4a43]" : "text-white/68")}>
        Choose your location, then continue with Google Reserve.
      </p>

      <div className="mt-5 grid gap-2 sm:grid-cols-2" role="tablist" aria-label="Choose a Roma location">
        {sortedLocations.map((location) => (
          <button
            key={location.slug}
            type="button"
            role="tab"
            aria-selected={location.slug === locationSlug}
            onClick={() => setLocationSlug(location.slug)}
            className={cn(
              "min-h-11 rounded-[10px] border px-3 text-sm font-semibold",
              location.slug === locationSlug
                ? "border-[var(--roma-terracotta)] bg-[var(--roma-terracotta)] text-[#0d0d0d]"
                : isLight
                  ? "border-[#d8d8d8] bg-white text-[#151515]"
                  : "border-white/20 bg-white/5 text-white",
            )}
          >
            {location.eyebrow}
          </button>
        ))}
      </div>

      <a
        href={selectedLocation.reserveUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-flex min-h-12 w-full items-center justify-center rounded-[10px] bg-[#b91c1c] px-5 text-sm font-semibold text-white hover:bg-[#cf1f1f]"
        onClick={() =>
          analytics.reservationClicked("roma_reservation_widget", {
            label: selectedLocation.slug,
            cta_text: "Reserve Now",
            destination_url: selectedLocation.reserveUrl,
          })
        }
      >
        Reserve Now
      </a>
    </section>
  );
}
